/**
 * Turns a `get_delivery_options` tool result into something renderable.
 *
 * Every shape here was read off real responses from api.acceptance.myparcel.nl rather
 * than assumed, because the last round of bugs in this path were all assumptions that
 * happened to look plausible. The ones worth knowing:
 *
 * - `location.distance` is a string in metres, and the list is NOT sorted by it.
 * - `location.type` is null for every location, so it cannot be used to tell a parcel
 *   locker from a shop. A 24/7 location shows up as 00:00–23:59 on all seven days.
 * - Delivery prices are cents (1313 = € 13,13, matching the € 14,15 morning surcharge
 *   seen in production). A pickup `price` is a bare number, not a `{currency, amount}`
 *   object, and was 0 for all 20 locations on acceptance — see `pickupPriceCents`.
 * - An `opening_hours` day can be an empty array, meaning closed.
 */

/** Delivery type ids seen in responses: 1 and 2 on acceptance, 3 in production. */
const DELIVERY_TYPE_LABELS: Record<number, string> = {
  1: 'Ochtendlevering',
  2: 'Standaard',
  3: 'Avondlevering',
};

const DAY_KEYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

const DAY_LABELS: Record<(typeof DAY_KEYS)[number], string> = {
  monday: 'ma',
  tuesday: 'di',
  wednesday: 'wo',
  thursday: 'do',
  friday: 'vr',
  saturday: 'za',
  sunday: 'zo',
};

export type Slot = {
  typeId: number;
  label: string;
  start: string;
  end: string;
  cents: number;
};

export type DeliveryDay = {
  /** ISO date, `2026-08-20`. */
  date: string;
  slots: Slot[];
};

export type OpeningDay = {
  label: string;
  /** Empty when the location is closed that day. */
  ranges: { start: string; end: string }[];
};

export type PickupPoint = {
  name: string;
  address: string;
  city: string;
  distanceMetres: number;
  lat: number;
  lng: number;
  /** First moment the parcel can be collected, or null when absent. */
  availableFrom: string | null;
  priceCents: number | null;
  openingHours: OpeningDay[];
  alwaysOpen: boolean;
};

export type DeliveryOptionsView = {
  address: string | null;
  /** The raw request filters, for handing the address to the interactive widget. */
  filters: { cc: string; postalCode: string; number: string } | null;
  days: DeliveryDay[];
  /** Delivery types this address does not offer at all, e.g. evening delivery. */
  unavailableTypes: string[];
  pickups: PickupPoint[];
  problems: unknown[] | null;
  carrierWarning: string | null;
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

const asString = (value: unknown): string => (typeof value === 'string' ? value : '');

/** `08:00:00` and `2026-08-20 16:00:00.000000` both yield `08:00` / `16:00`. */
function timeOf(value: unknown): string {
  const text = asString(value);
  const match = /(\d{2}):(\d{2})/.exec(text.includes(' ') ? text.slice(11) : text);
  return match ? `${match[1]}:${match[2]}` : '';
}

function dateOf(value: unknown): string {
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(asString(value));
  return match ? match[1] : '';
}

/**
 * Returns null rather than throwing, so a result this parser does not recognise falls
 * back to the raw JSON view instead of blanking the card.
 */
export function parseDeliveryOptions(text: string): DeliveryOptionsView | null {
  let payload: unknown;
  try {
    payload = JSON.parse(text);
  } catch {
    return null;
  }

  const root = asRecord(payload);
  if (!root || (!Array.isArray(root['delivery_options']) && !Array.isArray(root['pickup_locations']))) {
    return null;
  }

  const days = asArray(root['delivery_options'])
    .map((entry) => parseDay(entry))
    .filter((day): day is DeliveryDay => day !== null);

  const offered = new Set(days.flatMap((day) => day.slots.map((slot) => slot.typeId)));

  const filters = asRecord(root['applied_filters']);

  return {
    address: formatAddress(filters),
    filters:
      filters && asString(filters['postal_code']) && asString(filters['number'])
        ? {
            cc: asString(filters['cc']) || 'NL',
            postalCode: asString(filters['postal_code']),
            number: asString(filters['number']),
          }
        : null,
    days,
    unavailableTypes: Object.entries(DELIVERY_TYPE_LABELS)
      .filter(([id]) => !offered.has(Number(id)))
      .map(([, label]) => label),
    pickups: asArray(root['pickup_locations'])
      .map((entry) => parsePickup(entry))
      .filter((point): point is PickupPoint => point !== null)
      // The API returns these in no particular order; nearest-first is the only
      // ordering a reader can act on.
      .sort((a, b) => a.distanceMetres - b.distanceMetres),
    problems: Array.isArray(root['problems']) ? root['problems'] : null,
    carrierWarning: typeof root['carrier_warning'] === 'string' ? root['carrier_warning'] : null,
  };
}

function parseDay(entry: unknown): DeliveryDay | null {
  const record = asRecord(entry);
  const date = dateOf(record?.['date']);
  if (!record || !date) {
    return null;
  }

  const slots = asArray(record['time'])
    .map((raw): Slot | null => {
      const slot = asRecord(raw);
      if (!slot) {
        return null;
      }
      const typeId = typeof slot['type'] === 'number' ? slot['type'] : 0;
      const price = asRecord(slot['price']);
      return {
        typeId,
        // Fall back to the API's own `price_comment` when the id is unknown to us, so a
        // new delivery type shows up with a name instead of vanishing.
        label: DELIVERY_TYPE_LABELS[typeId] ?? asString(slot['price_comment']) ?? 'Onbekend',
        start: timeOf(slot['start']),
        end: timeOf(slot['end']),
        cents: typeof price?.['amount'] === 'number' ? price['amount'] : 0,
      };
    })
    .filter((slot): slot is Slot => slot !== null)
    .sort((a, b) => a.cents - b.cents || a.start.localeCompare(b.start));

  return { date, slots };
}

function parsePickup(entry: unknown): PickupPoint | null {
  const record = asRecord(entry);
  const location = asRecord(record?.['location']);
  const address = asRecord(record?.['address']);
  if (!record || !location || !address) {
    return null;
  }

  const lat = Number(location['latitude']);
  const lng = Number(location['longitude']);
  const first = asRecord(asRecord(asArray(record['possibilities'])[0])?.['moment']);

  return {
    name: asString(location['location_name']) || 'Ophaalpunt',
    address: [asString(address['street']), asString(address['number']) + asString(address['number_suffix'])]
      .join(' ')
      .trim(),
    city: asString(address['city']),
    // A string in metres upstream; NaN sorts last rather than corrupting the order.
    distanceMetres: Number.isFinite(Number(location['distance'])) ? Number(location['distance']) : Infinity,
    lat: Number.isFinite(lat) ? lat : NaN,
    lng: Number.isFinite(lng) ? lng : NaN,
    availableFrom: asRecord(first?.['start']) ? asString(asRecord(first?.['start'])?.['date']) : null,
    priceCents: pickupPriceCents(asArray(record['possibilities'])[0]),
    openingHours: parseOpeningHours(asRecord(location['opening_hours'])),
    alwaysOpen: isAlwaysOpen(asRecord(location['opening_hours'])),
  };
}

/**
 * A pickup `price` is a bare number where delivery uses `{currency, amount}` in cents.
 * Every location on acceptance returned 0, so the unit could not be confirmed there;
 * cents is assumed for consistency with the rest of the API, and a non-zero value is
 * rendered as a difference rather than a total so a wrong unit stays visible as an
 * implausible number instead of quietly misstating a price.
 */
function pickupPriceCents(possibility: unknown): number | null {
  const price = asRecord(possibility)?.['price'];
  return typeof price === 'number' && price !== 0 ? price : null;
}

function parseOpeningHours(hours: Record<string, unknown> | null): OpeningDay[] {
  if (!hours) {
    return [];
  }

  return DAY_KEYS.map((key) => ({
    label: DAY_LABELS[key],
    ranges: asArray(hours[key])
      .map((raw) => {
        const range = asRecord(raw);
        return {
          start: timeOf(asRecord(range?.['start'])?.['date']),
          end: timeOf(asRecord(range?.['end'])?.['date']),
        };
      })
      .filter((range) => range.start !== '' && range.end !== ''),
  }));
}

/** 24/7 locations report 00:00–23:59 on all seven days; there is no flag for it. */
function isAlwaysOpen(hours: Record<string, unknown> | null): boolean {
  const days = parseOpeningHours(hours);
  return (
    days.length === DAY_KEYS.length &&
    days.every((day) => day.ranges.some((range) => range.start === '00:00' && range.end >= '23:59'))
  );
}

function formatAddress(filters: Record<string, unknown> | null): string | null {
  if (!filters) {
    return null;
  }
  const parts = [asString(filters['postal_code']).toUpperCase(), asString(filters['number'])].filter(Boolean);
  if (parts.length === 0) {
    return null;
  }
  const cc = asString(filters['cc']).toUpperCase();
  return cc ? `${parts.join(' ')} (${cc})` : parts.join(' ');
}

/**
 * One line for the common cases: 24/7, or the same hours every day. Anything more
 * varied returns null, and the caller shows the full week instead of compressing it
 * into something that reads as precise but is not.
 */
export function openingSummary(point: PickupPoint): string | null {
  if (point.alwaysOpen) {
    return '24/7 open';
  }

  const rendered = point.openingHours.map((day) =>
    day.ranges.map((range) => `${range.start}-${range.end}`).join(', '),
  );
  if (rendered.length === 0 || rendered.some((day) => day === '')) {
    return null;
  }

  return rendered.every((day) => day === rendered[0]) ? `Dagelijks ${rendered[0].replace('-', '–')}` : null;
}

export function formatEuro(cents: number): string {
  return (cents / 100).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });
}

/** `2026-08-20` → `wo 20 aug`. Parsed as a plain date, so no timezone shifts it. */
export function formatDayLabel(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date
    .toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })
    .replace('.', '');
}

export function formatDistance(metres: number): string {
  if (!Number.isFinite(metres)) {
    return '';
  }
  return metres < 1000
    ? `${Math.round(metres)} m`
    : `${(metres / 1000).toLocaleString('nl-NL', { maximumFractionDigits: 1 })} km`;
}

/** `2026-08-20 16:00:00.000000` → `wo 20 aug vanaf 16:00`. */
export function formatAvailableFrom(raw: string | null): string {
  if (!raw) {
    return '';
  }
  const date = dateOf(raw);
  const time = timeOf(raw);
  if (!date) {
    return '';
  }
  return time ? `${formatDayLabel(date)} vanaf ${time}` : formatDayLabel(date);
}

/**
 * What the MyParcel widget emits on every choice. Only the fields this app reads are
 * declared; the exact type lives in @myparcel/delivery-options and is re-checked where
 * the widget is mounted.
 */
export type WidgetSelection = {
  carrier: string;
  date?: string;
  deliveryType: string;
  isPickup: boolean;
  packageType: string;
  shipmentOptions?: Record<string, unknown>;
  pickupLocation?: {
    locationName: string;
    locationCode: string;
    retailNetworkId: string;
    street: string;
    number: string;
    postalCode: string;
    city: string;
  };
};

const DELIVERY_TYPE_NL: Record<string, string> = {
  morning: 'ochtendlevering',
  standard: 'standaardlevering',
  evening: 'avondlevering',
  pickup: 'ophalen',
};

/**
 * Turns a widget selection into the chat message that carries the choice back to the
 * model. Two parts: `display` is the one sentence the person sees in their own bubble,
 * `message` additionally carries the structured selection so the model copies exact
 * values (location codes, dates) instead of re-deriving them from prose — re-deriving
 * is how a wrong pickup point ends up on a label. The JSON is deliberately kept out of
 * `display`: it is plumbing, not something the person said.
 */
export function formatSelectionMessage(selection: WidgetSelection): {
  display: string;
  message: string;
} {
  const enabled = Object.entries(selection.shipmentOptions ?? {})
    .filter(([, value]) => value === true)
    .map(([key]) => key);
  const extras = enabled.length > 0 ? ` met ${enabled.join(' en ')}` : '';
  const day = selection.date ? ` op ${selection.date.slice(0, 10)}` : '';

  const line = selection.isPickup
    ? `Ik kies ophalen bij ${selection.pickupLocation?.locationName ?? 'een ophaalpunt'} ` +
      `(${selection.pickupLocation?.street ?? ''} ${selection.pickupLocation?.number ?? ''}, ` +
      `${selection.pickupLocation?.city ?? ''})${day} via ${selection.carrier}${extras}.`
    : `Ik kies ${DELIVERY_TYPE_NL[selection.deliveryType] ?? selection.deliveryType}${day} ` +
      `via ${selection.carrier}${extras}.`;

  return {
    display: line,
    message: `${line}

Gekozen bezorgoptie (gebruik deze waarden exact):
\`\`\`json
${JSON.stringify(selection, null, 2)}
\`\`\``,
  };
}

/** Deep link that needs no API key, unlike an embedded Google map. */
export function googleMapsUrl(point: PickupPoint): string {
  const query = Number.isFinite(point.lat) && Number.isFinite(point.lng)
    ? `${point.lat},${point.lng}`
    : `${point.name} ${point.address} ${point.city}`;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}
